import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { FormPromptDialog } from '@/components/ui/TextPromptDialog';
import { LoadingState } from '@/components/ui/States';
import { api, ApiError } from '@/lib/api/client';
import { authFetch } from '@/api';
import { branchKey, keys } from '@/lib/queryKeys';
import { toast } from '@/hooks/useToast';
import { useVendors } from '../useVendors';

type Reason = 'damaged' | 'expired' | 'short_expiry' | 'wrong_item' | 'excess' | 'recall' | 'other';
const REASONS: { value: Reason; label: string }[] = [
  { value: 'damaged', label: 'Damaged' }, { value: 'expired', label: 'Expired' }, { value: 'short_expiry', label: 'Short expiry' },
  { value: 'wrong_item', label: 'Wrong item / batch' }, { value: 'excess', label: 'Excess supply' }, { value: 'recall', label: 'Recall' },
  { value: 'other', label: 'Other (say why in notes)' },
];

interface ReturnRow {
  id: number; return_number: string; vendor_name: string; status: 'dispatched' | 'credited'; dispatched_at: string;
  dispatched_by: string | null; total_value: string; total_gst: string; line_count: number;
  credit_note_number: string | null; credit_amount: string | null;
}
interface Batch {
  inventory_id: number; batch_number: string; expiry_date: string; quantity_available: number; purchase_price: string | null;
  store_id: number; store_name: string | null; generic_name: string; brand_name: string | null; strength: string | null; supplied_by: number | null;
}
interface Line { batch: Batch; quantity: string; reason: Reason | '' }

const listKey = () => branchKey('pharmacy', 'supplier-returns');
const money = (v: number | string) => `₹${Number(v).toFixed(2)}`;
const day = (v: string) => new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

async function printDebitNote(id: number) {
  const res = await authFetch(`/api/pharmacy-supplier-returns/${id}/note`);
  if (!res.ok) throw new Error(`Could not open the debit note (HTTP ${res.status})`);
  const html = await res.text();
  document.getElementById('hms-debit-note-frame')?.remove();
  const frame = document.createElement('iframe');
  frame.id = 'hms-debit-note-frame';
  frame.title = 'Debit note';
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('style', 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;');
  frame.onload = () => { frame.contentWindow?.focus(); frame.contentWindow?.print(); };
  frame.srcdoc = html;
  document.body.appendChild(frame);
}

/**
 * Pharmacy → Procurement → ↩ Supplier returns (Phase 1 G14): goods going back
 * to a supplier as a numbered debit note. Stock leaves the chosen batches when
 * it's dispatched; "Record credit" closes it when the supplier's credit note arrives.
 */
export default function SupplierReturnsPanel() {
  const qc = useQueryClient();
  const vendors = useVendors();
  const list = useQuery({ queryKey: listKey(), queryFn: () => api.get<ReturnRow[]>('/pharmacy-supplier-returns') });
  const [creating, setCreating] = useState(false);
  const [crediting, setCrediting] = useState<ReturnRow | null>(null);
  const [creditError, setCreditError] = useState<string | null>(null);

  const credit = useMutation({
    mutationFn: ({ id, values }: { id: number; values: Record<string, string> }) =>
      api.post(`/pharmacy-supplier-returns/${id}/credit`, { credit_note_number: values.number, credit_amount: Number(values.amount) }),
    onSuccess: () => { setCrediting(null); toast({ title: 'Credit recorded', variant: 'success' }); qc.invalidateQueries({ queryKey: listKey() }); },
    onError: (e) => setCreditError(e instanceof ApiError || e instanceof Error ? e.message : 'Not saved'),
  });

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold">Returns to suppliers</div>
        {!creating && <Button size="sm" onClick={() => setCreating(true)}>+ New return</Button>}
      </div>

      {creating && (
        <NewReturnForm
          vendors={(vendors.data ?? []).filter((v) => v.active !== false)}
          onCancel={() => setCreating(false)}
          onDone={() => { setCreating(false); qc.invalidateQueries({ queryKey: listKey() }); qc.invalidateQueries({ queryKey: keys.pharmacy.inventory() }); }}
        />
      )}

      {list.isLoading ? <LoadingState /> : (list.data ?? []).length === 0 ? (
        <div className="py-6 text-center text-[13px] text-ink-muted">No returns yet.</div>
      ) : (
        <ul className="divide-y divide-border rounded-[var(--radius)] border border-border text-[12.5px]">
          {(list.data ?? []).map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <span>
                <span className="font-mono font-semibold">{r.return_number}</span> · {r.vendor_name} · {day(r.dispatched_at)}
                <span className="text-ink-muted"> · {r.line_count} line(s) · {money(Number(r.total_value) + Number(r.total_gst))} incl. GST</span>
              </span>
              <span className="flex items-center gap-1.5">
                {r.status === 'credited'
                  ? <Badge variant="success">Credited {r.credit_note_number} · {money(r.credit_amount ?? 0)}</Badge>
                  : <Badge variant="warn">Awaiting supplier credit</Badge>}
                <Button size="sm" variant="ghost" onClick={() => printDebitNote(r.id).catch((e: Error) => toast({ title: 'Not opened', description: e.message, variant: 'danger' }))}>
                  <Printer className="h-3.5 w-3.5" /> Debit note
                </Button>
                {r.status === 'dispatched' && <Button size="sm" variant="ghost" onClick={() => { setCreditError(null); setCrediting(r); }}>Record credit…</Button>}
              </span>
            </li>
          ))}
        </ul>
      )}

      <FormPromptDialog
        open={!!crediting}
        title={`Supplier credit for ${crediting?.return_number ?? ''}`}
        description={crediting ? `Debit note value ${money(Number(crediting.total_value) + Number(crediting.total_gst))} incl. GST.` : ''}
        fields={[
          { name: 'number', label: 'Supplier’s credit note number', required: true },
          { name: 'amount', label: 'Credit amount (₹)', required: true,
            initialValue: crediting ? String((Number(crediting.total_value) + Number(crediting.total_gst)).toFixed(2)) : '' },
        ]}
        confirmLabel="Record credit"
        pending={credit.isPending}
        error={creditError}
        onClose={() => setCrediting(null)}
        onConfirm={(values) => crediting && credit.mutate({ id: crediting.id, values })}
      />
    </Card>
  );
}

function NewReturnForm({ vendors, onCancel, onDone }: { vendors: { id: number; name: string }[]; onCancel: () => void; onDone: () => void }) {
  const [vendorId, setVendorId] = useState('');
  const [q, setQ] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const batches = useQuery({
    queryKey: branchKey('pharmacy', 'supplier-returns', 'batches', vendorId, q),
    queryFn: () => api.get<Batch[]>('/pharmacy-supplier-returns/batches', { query: { vendor_id: vendorId || undefined, q: q || undefined } }),
    enabled: !!vendorId,
  });
  const submit = useMutation({
    mutationFn: () => api.post<{ return_number: string }>('/pharmacy-supplier-returns', {
      vendor_id: Number(vendorId), notes: notes.trim() || undefined,
      items: lines.map((l) => ({ inventory_id: l.batch.inventory_id, quantity: Number(l.quantity), reason: l.reason })),
    }),
    onSuccess: (r) => { toast({ title: `${r.return_number} dispatched`, description: 'Stock has been reduced. Print the debit note for the supplier.', variant: 'success' }); onDone(); },
    onError: (e) => setError(e instanceof ApiError || e instanceof Error ? e.message : 'Not saved'),
  });

  const add = (b: Batch) => {
    if (lines.some((l) => l.batch.inventory_id === b.inventory_id)) return;
    if (lines.length && lines[0]!.batch.store_id !== b.store_id) { setError('A return comes from one store. Make a separate return for the other store.'); return; }
    setError(null);
    setLines([...lines, { batch: b, quantity: '', reason: '' }]);
  };
  const go = () => {
    if (!vendorId) { setError('Choose the supplier'); return; }
    if (!lines.length) { setError('Add at least one batch'); return; }
    for (const l of lines) {
      const n = Number(l.quantity);
      if (!Number.isInteger(n) || n <= 0 || n > l.batch.quantity_available) { setError(`${l.batch.generic_name} ${l.batch.batch_number}: quantity must be 1 to ${l.batch.quantity_available}`); return; }
      if (!l.reason) { setError(`${l.batch.generic_name} ${l.batch.batch_number}: choose a reason`); return; }
    }
    if (lines.some((l) => l.reason === 'other') && !notes.trim()) { setError('Say why in the notes (a line is "Other")'); return; }
    setError(null);
    submit.mutate();
  };
  const total = lines.reduce((n, l) => n + (Number(l.quantity) || 0) * Number(l.batch.purchase_price || 0), 0);

  return (
    <div className="mb-4 rounded-[var(--radius)] border border-line bg-surface p-3">
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr]">
        <FormField label="Supplier">
          <NativeSelect value={vendorId} onChange={(e) => { setVendorId(e.target.value); setLines([]); }}>
            <option value="">Choose…</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </NativeSelect>
        </FormField>
        <FormField label="Find a batch" hint="This supplier’s batches are listed first.">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Medicine or batch number" disabled={!vendorId} />
        </FormField>
      </div>
      {vendorId && (
        <ul className="mt-2 max-h-48 overflow-y-auto rounded-[var(--radius)] border border-border text-[12px]">
          {(batches.data ?? []).map((b) => (
            <li key={b.inventory_id} className="flex items-center justify-between gap-2 border-b border-border px-2.5 py-1.5 last:border-0">
              <span>
                {b.generic_name}{b.strength ? ` ${b.strength}` : ''} · <span className="font-mono">{b.batch_number}</span> · exp {day(b.expiry_date)} · {b.quantity_available} in {b.store_name ?? 'store'}
                {b.supplied_by === Number(vendorId) && <Badge variant="info" className="ml-1.5">from this supplier</Badge>}
              </span>
              <Button size="sm" variant="ghost" onClick={() => add(b)} disabled={lines.some((l) => l.batch.inventory_id === b.inventory_id)}>Add</Button>
            </li>
          ))}
          {batches.data && batches.data.length === 0 && <li className="px-2.5 py-2 text-ink-muted">No batches in stock match.</li>}
        </ul>
      )}
      {lines.length > 0 && (
        <div className="mt-3 flex flex-col gap-1.5">
          {lines.map((l, i) => (
            <div key={l.batch.inventory_id} className="grid grid-cols-[1fr_90px_170px_auto] items-center gap-2 text-[12px]">
              <span>{l.batch.generic_name} · <span className="font-mono">{l.batch.batch_number}</span> <span className="text-ink-muted">({l.batch.quantity_available} in stock)</span></span>
              <Input type="number" min={1} max={l.batch.quantity_available} value={l.quantity} placeholder="Qty" className="text-xs"
                onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} />
              <NativeSelect value={l.reason} className="text-xs" aria-label="Reason"
                onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, reason: e.target.value as Reason } : x)))}>
                <option value="">Reason…</option>
                {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </NativeSelect>
              <Button size="sm" variant="ghost" onClick={() => setLines(lines.filter((_, j) => j !== i))} aria-label="Remove line">✕</Button>
            </div>
          ))}
          <div className="text-right text-[12px] text-ink-muted">Value at cost {money(total)} (GST added on the debit note)</div>
        </div>
      )}
      <FormField label="Notes" className="mt-2">
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Cold chain broken in transit; supplier's rep informed" />
      </FormField>
      {error && <div className="mt-2 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={go} loading={submit.isPending}>Dispatch return</Button>
      </div>
    </div>
  );
}
